import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Share2, Copy, Check, Trash2, Plus, ExternalLink,
  GripVertical, MessageSquare, Presentation, Home, FolderPlus, X, MoveHorizontal,
  Image as ImageIcon, FileUp, Clock, FileDown
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Common room/environment presets
const ENVIRONMENT_PRESETS = [
  'Kitchen', 'Master Bathroom', 'Guest Bathroom', 'Living Room', 'Dining Room',
  'Master Bedroom', 'Entryway', 'Hallway', 'Outdoor', 'Pool Area', 'Fireplace', 
  'Bar', 'Wine Cellar', 'Powder Room', 'Laundry', 'Mudroom'
];

const BoardDetail = () => {
  const { boardId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showAddEnv, setShowAddEnv] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [itemToMove, setItemToMove] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [presentUrl, setPresentUrl] = useState('');
  const [copied, setCopied] = useState(false);
  const [newEnvName, setNewEnvName] = useState('');
  const [clientUploads, setClientUploads] = useState([]);
  const [projectFiles, setProjectFiles] = useState([]);
  const [showClientUploads, setShowClientUploads] = useState(false);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [selectedUpload, setSelectedUpload] = useState(null);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    fetchBoard();
  }, [boardId]);

  const fetchBoard = async () => {
    try {
      const response = await axios.get(`${API}/boards/${boardId}`, { withCredentials: true });
      setBoard(response.data);
      if (response.data.share_token) {
        setShareUrl(`${window.location.origin}/review/${response.data.share_token}`);
        setPresentUrl(`${window.location.origin}/present/${response.data.share_token}`);
      }
    } catch (error) {
      console.error('Failed to fetch board:', error);
      toast.error('Project not found');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const shareBoard = async () => {
    try {
      const response = await axios.post(`${API}/boards/${boardId}/share`, {}, { withCredentials: true });
      const baseUrl = window.location.origin;
      setShareUrl(`${baseUrl}/review/${response.data.share_token}`);
      setPresentUrl(`${baseUrl}/present/${response.data.share_token}`);
      setShowShare(true);
    } catch (error) {
      toast.error('Failed to generate share link');
    }
  };

  const fetchClientUploads = async () => {
    try {
      const response = await axios.get(`${API}/uploads/boards/${boardId}/client-inspirations`, { withCredentials: true });
      setClientUploads(response.data);
    } catch (error) {
      console.error('Failed to fetch client uploads:', error);
    }
  };

  const fetchProjectFiles = async () => {
    try {
      const response = await axios.get(`${API}/files/boards/${boardId}`, { withCredentials: true });
      setProjectFiles(response.data);
    } catch (error) {
      console.error('Failed to fetch project files:', error);
    }
  };

  const addCommentToUpload = async (uploadId) => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API}/uploads/boards/${boardId}/client-inspirations/${uploadId}/comment`, 
        { text: newComment },
        { withCredentials: true }
      );
      toast.success('Comment added');
      setNewComment('');
      fetchClientUploads();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  // Load client uploads when tab is opened
  useEffect(() => {
    if (showClientUploads && boardId) {
      fetchClientUploads();
      fetchProjectFiles();
    }
  }, [showClientUploads, boardId]);

  const copyUrl = async (url) => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const addEnvironment = async (name) => {
    try {
      const response = await axios.post(`${API}/boards/${boardId}/environments`, 
        { name }, 
        { withCredentials: true }
      );
      setBoard(prev => ({
        ...prev,
        environments: [...(prev.environments || []), response.data.environment]
      }));
      toast.success(`${name} added`);
      setShowAddEnv(false);
      setNewEnvName('');
    } catch (error) {
      toast.error('Failed to add environment');
    }
  };

  const removeEnvironment = async (environmentId) => {
    if (!window.confirm('Remove this room and all its materials?')) return;
    try {
      await axios.delete(`${API}/boards/${boardId}/environments/${environmentId}`, { withCredentials: true });
      setBoard(prev => ({
        ...prev,
        environments: prev.environments.filter(e => e.environment_id !== environmentId),
        items: prev.items.filter(i => i.environment_id !== environmentId)
      }));
      toast.success('Room removed');
    } catch (error) {
      toast.error('Failed to remove room');
    }
  };

  const removeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/boards/${boardId}/items/${itemId}`, { withCredentials: true });
      setBoard(prev => ({
        ...prev,
        items: prev.items.filter(i => i.item_id !== itemId)
      }));
      toast.success('Material removed');
    } catch (error) {
      toast.error('Failed to remove material');
    }
  };

  const moveItemToRoom = async (itemId, environmentId) => {
    try {
      await axios.patch(`${API}/boards/${boardId}/items/${itemId}/move`, 
        { environment_id: environmentId },
        { withCredentials: true }
      );
      setBoard(prev => ({
        ...prev,
        items: prev.items.map(i => 
          i.item_id === itemId ? { ...i, environment_id: environmentId } : i
        )
      }));
      toast.success('Material moved successfully');
      setShowMoveDialog(false);
      setItemToMove(null);
    } catch (error) {
      toast.error('Failed to move material');
    }
  };

  const openMoveDialog = (item) => {
    setItemToMove(item);
    setShowMoveDialog(true);
  };

  // Group items by environment
  const getItemsByEnvironment = (envId) => {
    return board?.items?.filter(i => i.environment_id === envId) || [];
  };

  const getGeneralItems = () => {
    return board?.items?.filter(i => !i.environment_id) || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!board) return null;

  const generalItems = getGeneralItems();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Content with padding for fixed header */}
      <main className="max-w-[1800px] mx-auto px-6 md:px-12 py-12 pt-28">
        {/* Board Actions Bar */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <button 
            onClick={() => navigate('/projects')} 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Projects
          </button>
          <div className="flex items-center gap-3">
            {shareUrl && (
              <>
                <Button 
                  onClick={() => window.open(presentUrl, '_blank')}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="present-btn"
                >
                  <Presentation className="w-4 h-4" /> Present
                </Button>
                <Button 
                  onClick={() => window.open(`${API}/review/${board.share_token}/pdf`, '_blank')}
                  variant="outline" 
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="export-pdf-btn"
                >
                  <FileDown className="w-4 h-4" /> PDF
                </Button>
              </>
            )}
            <Button 
              onClick={shareBoard}
              variant="outline" 
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="share-board-btn"
            >
              <Share2 className="w-4 h-4" /> Share
            </Button>
          </div>
        </div>

        {/* Board Info */}
        <div className="mb-12">
          <p className="caption-text text-muted-foreground mb-2">
            {board.project_name || 'Project'}
            {board.city && ` · ${board.city}`}
          </p>
          <h1 className="heading-display text-4xl md:text-5xl font-medium mb-4" data-testid="board-name">
            {board.name}
          </h1>
          {board.notes && (
            <p className="body-text text-muted-foreground max-w-2xl">{board.notes}</p>
          )}
        </div>

        {/* Environments Section */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <h2 className="heading-display text-2xl font-medium">Rooms & Environments</h2>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowClientUploads(true)}
              className="btn-secondary"
            >
              <ImageIcon className="w-4 h-4 mr-2" /> Client Uploads
              {clientUploads.length > 0 && (
                <span className="ml-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                  {clientUploads.length}
                </span>
              )}
            </Button>
            <Dialog open={showAddEnv} onOpenChange={setShowAddEnv}>
              <DialogTrigger asChild>
                <Button variant="outline" className="btn-secondary" data-testid="add-room-btn">
                  <FolderPlus className="w-4 h-4 mr-2" /> Add Room
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="heading-display text-xl">Add Room/Environment</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="caption-text mb-2 block">Quick Add</Label>
                  <div className="flex flex-wrap gap-2">
                    {ENVIRONMENT_PRESETS.map(preset => (
                      <button
                        key={preset}
                        onClick={() => addEnvironment(preset)}
                        className="px-3 py-1 text-xs border border-border hover:bg-secondary transition-colors"
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-4 bg-background text-xs text-muted-foreground">Or custom</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newEnvName}
                    onChange={(e) => setNewEnvName(e.target.value)}
                    placeholder="Custom room name..."
                    className="input-field flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && newEnvName && addEnvironment(newEnvName)}
                  />
                  <Button 
                    onClick={() => newEnvName && addEnvironment(newEnvName)}
                    disabled={!newEnvName}
                    className="btn-primary"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Environments */}
        {board.environments?.length > 0 ? (
          <div className="space-y-12 mb-16">
            {board.environments.map((env) => {
              const envItems = getItemsByEnvironment(env.environment_id);
              return (
                <div key={env.environment_id} className="border border-border p-6" data-testid={`env-${env.environment_id}`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Home className="w-5 h-5 text-muted-foreground" />
                      <h3 className="heading-display text-xl font-medium">{env.name}</h3>
                      <span className="text-sm text-muted-foreground">
                        {envItems.length} material{envItems.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      onClick={() => removeEnvironment(env.environment_id)}
                      className="p-2 hover:bg-secondary text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {envItems.length === 0 ? (
                    <div className="py-8 text-center bg-secondary/30">
                      <p className="text-muted-foreground mb-4">No materials in this room yet</p>
                      <Link to="/materials">
                        <Button variant="outline" className="btn-secondary">
                          <Plus className="w-4 h-4 mr-2" /> Browse Materials
                        </Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {envItems.map((item, idx) => (
                        <MaterialCard 
                          key={item.item_id} 
                          item={item} 
                          index={idx}
                          onRemove={() => removeItem(item.item_id)}
                          onMove={() => openMoveDialog(item)}
                          hasRooms={board.environments?.length > 0}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-secondary/30 mb-12">
            <Home className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="heading-display text-xl font-medium mb-2">No rooms added yet</h3>
            <p className="text-muted-foreground mb-6">
              Organize your material selections by room (Kitchen, Bathroom, etc.)
            </p>
            <Button onClick={() => setShowAddEnv(true)} className="btn-primary">
              <FolderPlus className="w-4 h-4 mr-2" /> Add First Room
            </Button>
          </div>
        )}

        {/* General Items (not assigned to room) */}
        {generalItems.length > 0 && (
          <div className="mb-12">
            <h3 className="heading-display text-xl font-medium mb-6">General Materials</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {generalItems.map((item, idx) => (
                <MaterialCard 
                  key={item.item_id} 
                  item={item} 
                  index={idx}
                  onRemove={() => removeItem(item.item_id)}
                  onMove={() => openMoveDialog(item)}
                  hasRooms={board.environments?.length > 0}
                />
              ))}
            </div>
          </div>
        )}

        {/* Add More */}
        <Link 
          to="/materials" 
          className="flex flex-col items-center justify-center min-h-[200px] border-2 border-dashed border-border hover:border-primary/50 hover:bg-secondary/30 transition-colors"
          data-testid="add-more-materials"
        >
          <Plus className="w-8 h-8 text-muted-foreground mb-3" />
          <span className="caption-text text-muted-foreground">Browse & Add Materials</span>
        </Link>
      </main>

      {/* Share Dialog */}
      <Dialog open={showShare} onOpenChange={setShowShare}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl">Share Project</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-6">
            <div>
              <Label className="caption-text mb-2 block">Client Review Link</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Clients can review and provide feedback on materials
              </p>
              <div className="flex gap-2">
                <Input value={shareUrl} readOnly className="input-field flex-1" />
                <Button onClick={() => copyUrl(shareUrl)} variant="outline" className="btn-secondary">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div>
              <Label className="caption-text mb-2 block">Presentation Mode</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Full-screen immersive view for meetings and screen sharing
              </p>
              <div className="flex gap-2">
                <Input value={presentUrl} readOnly className="input-field flex-1" />
                <Button onClick={() => copyUrl(presentUrl)} variant="outline" className="btn-secondary">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="flex gap-2">
              <a 
                href={shareUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" /> Client View
              </a>
              <a 
                href={presentUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <Presentation className="w-4 h-4" /> Present
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move to Room Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl">Move to Room</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              Select where to move "{itemToMove?.material?.name}"
            </p>
            
            {/* General option */}
            <button
              onClick={() => moveItemToRoom(itemToMove?.item_id, null)}
              className={`w-full p-4 text-left border transition-colors ${
                !itemToMove?.environment_id 
                  ? 'border-primary bg-primary/5' 
                  : 'border-border hover:bg-secondary/50'
              }`}
            >
              <p className="font-medium">General</p>
              <p className="text-sm text-muted-foreground">Not assigned to a room</p>
            </button>
            
            {/* Room options */}
            {board?.environments?.map(env => (
              <button
                key={env.environment_id}
                onClick={() => moveItemToRoom(itemToMove?.item_id, env.environment_id)}
                className={`w-full p-4 text-left border transition-colors flex items-center gap-3 ${
                  itemToMove?.environment_id === env.environment_id 
                    ? 'border-primary bg-primary/5' 
                    : 'border-border hover:bg-secondary/50'
                }`}
              >
                <Home className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="font-medium">{env.name}</p>
                  {env.notes && <p className="text-sm text-muted-foreground">{env.notes}</p>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Uploads Dialog */}
      <Dialog open={showClientUploads} onOpenChange={setShowClientUploads}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="heading-display text-xl">Client Uploads</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {clientUploads.length === 0 ? (
              <div className="text-center py-12">
                <ImageIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No client uploads yet.</p>
                <p className="text-sm text-muted-foreground mt-2">
                  When your client uploads inspirational photos, they'll appear here.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {clientUploads.map(upload => (
                  <div key={upload.upload_id} className="border border-border bg-white">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={`data:image/jpeg;base64,${upload.image_base64}`}
                        alt="Client inspiration"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(upload.created_at).toLocaleDateString()}
                        </span>
                        {upload.room_name && (
                          <span className="text-xs bg-secondary px-2 py-1">{upload.room_name}</span>
                        )}
                      </div>
                      {upload.notes && (
                        <p className="text-sm text-muted-foreground mb-3">"{upload.notes}"</p>
                      )}
                      
                      {/* Designer Comments */}
                      {upload.designer_comments?.length > 0 && (
                        <div className="border-t border-border pt-3 mt-3 space-y-2">
                          {upload.designer_comments.map(comment => (
                            <div key={comment.comment_id} className="bg-secondary/50 p-2 text-sm">
                              <p className="font-medium text-xs mb-1">{comment.user_name}</p>
                              <p className="text-muted-foreground">{comment.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Add Comment */}
                      <div className="mt-3 flex gap-2">
                        <Input
                          placeholder="Add comment..."
                          value={selectedUpload === upload.upload_id ? newComment : ''}
                          onFocus={() => setSelectedUpload(upload.upload_id)}
                          onChange={(e) => {
                            setSelectedUpload(upload.upload_id);
                            setNewComment(e.target.value);
                          }}
                          className="flex-1 text-sm"
                        />
                        <Button 
                          size="sm"
                          onClick={() => addCommentToUpload(upload.upload_id)}
                          disabled={selectedUpload !== upload.upload_id || !newComment.trim()}
                        >
                          Send
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      <Footer />
    </div>
  );
};

// Material Card Component
const MaterialCard = ({ item, index, onRemove, onMove, hasRooms }) => {
  const statusColors = {
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
    request_alternative: 'bg-orange-100 text-orange-800',
    revised: 'bg-[var(--gold)]/20 text-[var(--gold)]'
  };

  return (
    <div className="group relative bg-white border border-border" data-testid={`board-item-${item.item_id}`}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
          <span className="caption-text text-muted-foreground">Option {index + 1}</span>
        </div>
        <div className="flex items-center gap-1">
          {hasRooms && (
            <button
              onClick={onMove}
              className="p-1 hover:bg-secondary transition-colors text-muted-foreground hover:text-primary"
              title="Move to room"
            >
              <MoveHorizontal className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={onRemove}
            className="p-1 hover:bg-secondary transition-colors text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <Link to={`/materials/${item.material_id}`}>
        <div className="aspect-square overflow-hidden">
          <img 
            src={item.material?.images?.[0] || '/placeholder.jpg'} 
            alt={item.material?.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      </Link>

      <div className="p-4">
        <p className="caption-text text-muted-foreground mb-1">{item.material?.stone_type}</p>
        <h3 className="heading-display text-lg font-medium mb-2">{item.material?.name}</h3>
        
        {/* Advisor Suggested Badge */}
        {item.advisor_suggested && (
          <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 mb-2">
            <MessageSquare className="w-3 h-3 mr-1" />
            Advisor Suggested
          </span>
        )}
        
        {item.application_area && (
          <p className="text-xs text-muted-foreground mb-2">
            Application: {item.application_area}
          </p>
        )}
        
        {item.material?.finishes?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {item.material.finishes.slice(0, 3).map(finish => (
              <span key={finish} className="text-xs text-muted-foreground px-2 py-0.5 bg-secondary">
                {finish}
              </span>
            ))}
          </div>
        )}

        {/* Client Status */}
        {item.client_status && item.client_status !== 'pending' && (
          <div className={`mt-3 pt-3 border-t border-border`}>
            <span className={`inline-flex items-center px-2 py-1 text-xs uppercase tracking-wider font-medium ${statusColors[item.client_status] || statusColors.pending}`}>
              {item.client_status === 'approved' && <Check className="w-3 h-3 mr-1" />}
              {item.client_status === 'rejected' && <X className="w-3 h-3 mr-1" />}
              {item.client_status.replace('_', ' ')}
              {item.client_rating && ` · ${item.client_rating}/5`}
            </span>
            {item.client_comment && (
              <p className="text-sm text-muted-foreground mt-2 italic">"{item.client_comment}"</p>
            )}
          </div>
        )}

        {item.notes && (
          <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border">
            <MessageSquare className="w-3 h-3 inline mr-1" />
            {item.notes}
          </p>
        )}
      </div>
    </div>
  );
};

export default BoardDetail;
